import { buildProviderOptions } from '../video/ui-options';
import { ceilCredits } from './credits';
import { mergeInlineMedia, defaultInlineMediaForMode } from '../inline-attachments';

type UpstreamNodeLike = {
  type?: string;
  data?: any;
};

const pickPositiveNumber = (...vals: any[]) => {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
};

/**
 * 构造注入到 providerOptions 的 `_media` / `_frameMode` 字段，供 getCost / getPassCount
 * 差异化计费（如视频编辑按输入视频真实时长计费）。仅用于计费，不应作为下游 generate 入参。
 */
export function buildBillingProviderOptions(params: {
  base?: Record<string, any>;
  videoCount: number;
  audioCount: number;
  videoDurationSeconds?: number | null;
  audioDurationSeconds?: number | null;
  frameMode?: string;
}): Record<string, any> {
  const videoDur = pickPositiveNumber(params.videoDurationSeconds);
  const audioDur = pickPositiveNumber(params.audioDurationSeconds);
  return {
    ...(params.base ?? {}),
    _media: {
      hasVideo: params.videoCount > 0,
      hasAudio: params.audioCount > 0,
      videoCount: params.videoCount,
      audioCount: params.audioCount,
      videoDurationSeconds: videoDur,
      audioDurationSeconds: audioDur,
    },
    _frameMode: params.frameMode ?? 'none',
  };
}

export function getUpstreamVideoDurationSeconds(upstreamNodes: UpstreamNodeLike[] | undefined) {
  const upstream = Array.isArray(upstreamNodes) ? upstreamNodes : [];
  for (const n of upstream) {
    if (!n?.data) continue;
    if (n.type === 'video') {
      const d = n.data as any;
      const picked = pickPositiveNumber(d.durationSeconds, d.generated?.durationSeconds, d.duration);
      if (picked) return picked;
    }
    if (n.type === 'upload') {
      const d = n.data as any;
      const ft = String(d?.fileType ?? '');
      if (ft === 'video') {
        const picked = pickPositiveNumber(d.durationSeconds);
        if (picked) return picked;
      }
    }
  }
  return undefined;
}

/**
 * 用于计费/预估：优先本节点时长，真实媒体时长通过 providerOptions._media 提供给模型计费函数。
 * 最终兜底到 model.durations[0] 或 defaultSeconds。
 */
export function getVideoBillingDurationSeconds(params: {
  model: any;
  nodeData: any;
  upstreamNodes?: UpstreamNodeLike[];
  defaultSeconds?: number;
}) {
  const { model, nodeData, defaultSeconds = 5 } = params;

  // 1) 本节点：用户选择的 duration 优先（与后端 billSeconds 对齐），
  //    durationSeconds（实际视频文件时长）仅作兜底（如上传节点无 duration 时）。
  let duration =
    pickPositiveNumber(nodeData?.duration, nodeData?.durationSeconds, nodeData?.generated?.durationSeconds) ??
    undefined;

  // 2) 兜底：model.durations[0] -> defaultSeconds
  if (!duration || !Number.isFinite(duration)) {
    const d0 =
      Array.isArray((model as any)?.durations) && (model as any).durations.length > 0
        ? (model as any).durations[0]
        : defaultSeconds;
    duration = pickPositiveNumber(d0, defaultSeconds) ?? defaultSeconds;
  }

  return Math.max(1, Math.ceil(Number(duration)));
}

function resolveVideoEstimateParams(params: {
  model: any;
  nodeData: any;
  upstreamNodes?: UpstreamNodeLike[];
}) {
  const { model, nodeData, upstreamNodes } = params;
  const provider = (model as any)?.providers?.[0];
  const duration = getVideoBillingDurationSeconds({ model, nodeData, upstreamNodes });
  const quality =
    nodeData?.quality ??
    nodeData?.resolution ??
    (Array.isArray((model as any)?.qualities) ? (model as any).qualities[0] : undefined);

  const modelId = nodeData?.model as string | undefined;
  let providerOptions: Record<string, any> | undefined;
  if (modelId) {
    providerOptions = buildProviderOptions(modelId, nodeData?.modelUi ?? {});
  }

  // 从上游节点和内联附件推断媒体输入，注入 _media 标识供 getCost / getPassCount 使用。
  // 口径与 handle-generate 组装 allVideos/allAudios 一致：按模型 inlineMedia 的 allow/maxCount
  // 截断（上游在前、内连在后），避免把不会实际发送的媒体计入预估。
  const upstream = Array.isArray(upstreamNodes) ? upstreamNodes : [];
  const inlineAtts: any[] = Array.isArray(nodeData?.inlineAttachments) ? nodeData.inlineAttachments : [];
  const frameMode = nodeData?.frameMode as string | undefined;

  const imCfg =
    mergeInlineMedia((model as any)?.inlineMedia, (model as any)?.configByMode?.[frameMode ?? 'none']?.inlineMedia) ??
    defaultInlineMediaForMode(frameMode ?? 'none');
  const capOf = (tc: any): number =>
    tc?.allow ? (Number.isFinite(Number(tc?.maxCount)) ? Number(tc.maxCount) : Infinity) : 0;

  const upstreamDur = (n: UpstreamNodeLike) => {
    const d = n.data as any;
    return pickPositiveNumber(d?.durationSeconds, d?.generated?.durationSeconds, d?.duration) ?? 0;
  };
  const inlineDur = (a: any) => pickPositiveNumber(a?.durationSeconds, a?.duration) ?? 0;

  const videoDurs = [
    ...upstream.filter(n => n.type === 'video' || (n.type === 'upload' && String(n.data?.fileType) === 'video')).map(upstreamDur),
    ...inlineAtts.filter((a: any) => a?.fileType === 'video' && a?.url).map(inlineDur),
  ].slice(0, capOf(imCfg.video));
  const audioDurs = [
    ...upstream.filter(n => n.type === 'audio' || (n.type === 'upload' && String(n.data?.fileType) === 'audio')).map(upstreamDur),
    ...inlineAtts.filter((a: any) => a?.fileType === 'audio' && a?.url).map(inlineDur),
  ].slice(0, capOf(imCfg.audio));

  const extra: Record<string, any> = {};

  if (videoDurs.length > 0 || audioDurs.length > 0) {
    const videoDur = videoDurs.reduce((s, d) => s + d, 0);
    const audioDur = audioDurs.reduce((s, d) => s + d, 0);

    extra._media = {
      hasVideo: videoDurs.length > 0,
      hasAudio: audioDurs.length > 0,
      videoCount: videoDurs.length,
      audioCount: audioDurs.length,
      videoDurationSeconds: videoDur > 0 ? videoDur : undefined,
      audioDurationSeconds: audioDur > 0 ? audioDur : undefined,
    };
  }

  if (frameMode) {
    extra._frameMode = frameMode;
  }

  if (Object.keys(extra).length > 0) {
    providerOptions = { ...(providerOptions ?? {}), ...extra };
  }

  return { provider, duration, quality, providerOptions };
}

export function estimateVideoUsdCost(params: {
  model: any;
  nodeData: any;
  upstreamNodes?: UpstreamNodeLike[];
}) {
  try {
    const { provider, duration, quality, providerOptions } = resolveVideoEstimateParams(params);
    if (!provider || typeof provider.getCost !== 'function') return null;

    const usd = Number(provider.getCost({ duration, quality, providerOptions }) ?? 0);
    if (!Number.isFinite(usd)) return null;
    return usd;
  } catch {
    return null;
  }
}

export function estimateVideoPassCount(params: {
  model: any;
  nodeData: any;
  upstreamNodes?: UpstreamNodeLike[];
}): number {
  try {
    const { provider, duration, quality, providerOptions } = resolveVideoEstimateParams(params);
    if (!provider || typeof provider.getPassCount !== 'function') return 1;
    const count = Number(provider.getPassCount({ duration, quality, providerOptions }) ?? 1);
    return Number.isFinite(count) && count > 0 ? ceilCredits(count) : 1;
  } catch {
    return 1;
  }
}

export function estimateVideoBilling(params: {
  model: any;
  nodeData: any;
  upstreamNodes?: UpstreamNodeLike[];
}): { usdCost: number | null; passCount: number } {
  try {
    const { provider, duration, quality, providerOptions } = resolveVideoEstimateParams(params);

    let usdCost: number | null = null;
    if (provider && typeof provider.getCost === 'function') {
      const usd = Number(provider.getCost({ duration, quality, providerOptions }) ?? 0);
      if (Number.isFinite(usd)) usdCost = usd;
    }

    let passCount = 1;
    if (provider && typeof provider.getPassCount === 'function') {
      const c = Number(provider.getPassCount({ duration, quality, providerOptions }) ?? 1);
      if (Number.isFinite(c) && c > 0) passCount = ceilCredits(c);
    }

    return { usdCost, passCount };
  } catch {
    return { usdCost: null, passCount: 1 };
  }
}
